import { useVirtualizer, VirtualItem, Virtualizer } from "@tanstack/react-virtual";
import { CSSProperties, memo, RefObject, useCallback, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import cx from "classnames";

import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import { OVERSCAN, ROW_HEIGHT_ESTIMATE } from "ds/components/TableSimple/constants";
import { ColumnWidthBounds } from "ds/components/TableSimple/Head";
import useTableSimpleEffectiveColumnPinning from "ds/components/TableSimple/hooks/useEffectiveColumnPinning";
import TableSimpleRow from "ds/components/TableSimple/Row";
import { TableCellPerColumn, TableSimpleStore } from "ds/components/TableSimple/types";
import useTypedContext from "hooks/useTypedContext";
import { TreeNode } from "utils/tree";
import { SpaceWithParentAccessLevel } from "views/Account/Spaces/types";

import { LEVEL_MARGIN } from "../constants";
import type { SpacesListCollapseAnchor, SpacesListGetCollapseAnchor } from "../index";
import { SpacesListContext } from "./Context";
import { calculateTreeVirtualPadding } from "./stickyHelpers";
import SpacesListStickyGroupRow from "./StickyGroupRow";
import styles from "./styles.module.css";
import { useSpacesListCollapseAnchor } from "./useCollapseAnchor";
import useSpacesListStickyRangeExtractor from "./useStickyRangeExtractor";

type SpacesListNode = TreeNode<SpaceWithParentAccessLevel>;

type SpacesListRowsProps = {
  nodes: SpacesListNode[];
  store: TableSimpleStore;
  cellPerColumn: TableCellPerColumn<SpacesListNode>;
  tableRef: RefObject<HTMLDivElement | null>;
  collapseAnchorRef: RefObject<SpacesListCollapseAnchor | null>;
  getCollapseAnchorRef: RefObject<SpacesListGetCollapseAnchor | null>;
  columnWidthBounds?: ColumnWidthBounds;
};

// An expanded group has a guide line running down to its children, so its border needs one extra
// level of inset to clear that line.
const getGuideInset = (level: number, isExpandedGroup: boolean): CSSProperties =>
  ({
    "--guide-inset": `calc(var(--semantic-spacing-x-large) + ${LEVEL_MARGIN} * ${
      level + (isExpandedGroup ? 1 : 0)
    })`,
  }) as CSSProperties;

// The scrollTop at which this header should start sliding up and out — i.e. when the first row
// after its subtree reaches it and pushes it away. `undefined` while no row follows the subtree.
const getEvictionAnchor = (
  measurements: readonly VirtualItem[],
  index: number,
  lastDescendant: number,
  level: number
): number | undefined => {
  const rowSize = measurements[index]?.size ?? ROW_HEIGHT_ESTIMATE;
  const rowAfterSubtree = measurements[lastDescendant + 1];

  return rowAfterSubtree ? rowAfterSubtree.start - rowSize + 1 - level * rowSize : undefined;
};

// Put the spacer height on an inner `table-cell`, not the `table-row`. Firefox sizes a table row
// from its cells, so a `table-row` with only a `height` collapses to nothing — which shifts the
// rows and flickers the sticky headers.
const SpacesListRowsSpacer = ({ height }: { height: number }) => (
  <div style={{ display: "table-row" }} aria-hidden="true">
    <div style={{ display: "table-cell", height }} />
  </div>
);

const SpacesListRows = ({
  nodes,
  store,
  cellPerColumn,
  tableRef,
  collapseAnchorRef,
  getCollapseAnchorRef,
  columnWidthBounds,
}: SpacesListRowsProps) => {
  const groupKeyboardProps = useRowKeyboardNavigation({ isGroup: true });
  const rowKeyboardProps = useRowKeyboardNavigation({ isGroup: false });
  const { expandedKeys } = useTypedContext(SpacesListContext);

  const { columns, isFirstColumnPinned } = store(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  // Same bounds the head sizes the columns with, so the rows agree on whether the first column can
  // be pinned at all — a tree deep enough to outgrow the table can't be.
  const { effectiveIsFirstColumnPinned } = useTableSimpleEffectiveColumnPinning(
    tableRef,
    isFirstColumnPinned,
    columnWidthBounds?.(columns[0], 0)?.minWidth
  );

  // The virtualizer doesn't exist yet when `rangeExtractor` is defined, so read its live
  // measurements through this ref.
  const virtualizerRef = useRef<Virtualizer<HTMLDivElement, Element> | null>(null);

  const { rangeExtractor, stickyGroupsVisibleOnTableRef, forcedStickyGroupsRef, openGroupStarts } =
    useSpacesListStickyRangeExtractor(nodes, tableRef, virtualizerRef);

  const getItemKey = useCallback((index: number) => nodes[index].id, [nodes]);

  // Don't call `virtualizer.measure()` on count changes: it resets `itemSizeCache` to the estimate,
  // so surviving rows re-measure and visibly shift ~1px on every collapse/expand.
  const virtualizer = useVirtualizer({
    count: nodes.length,
    getScrollElement: () => tableRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: OVERSCAN,
    getItemKey,
    rangeExtractor,
  });

  virtualizerRef.current = virtualizer;

  useSpacesListCollapseAnchor({
    nodes,
    tableRef,
    collapseAnchorRef,
    getCollapseAnchorRef,
    virtualizer,
  });

  const renderVirtualRow = useCallback(
    (virtualRow: VirtualItem) => {
      const node = nodes[virtualRow.index];
      const level = node.path.length - 1;
      const hasChildren = node.children.length > 0;
      const ariaLevel = level + 1;
      const ariaExpanded = hasChildren ? expandedKeys.has(node.id) : undefined;
      const lastStickyDescendant = stickyGroupsVisibleOnTableRef.current.get(virtualRow.index);
      const isActiveSticky = lastStickyDescendant !== undefined;
      const isExpandedGroup = openGroupStarts.has(virtualRow.index);
      const needsTreeBorder = level > 0 || isExpandedGroup;
      const guideStyle = needsTreeBorder ? getGuideInset(level, isExpandedGroup) : undefined;

      // Always render parents through the sticky group row, even when collapsed. Swapping to a
      // different component on toggle would remount the row and the toggle button would lose focus.
      if (hasChildren) {
        const evictionAnchor =
          lastStickyDescendant !== undefined
            ? getEvictionAnchor(
                virtualizer.measurementsCache,
                virtualRow.index,
                lastStickyDescendant,
                level
              )
            : undefined;

        return (
          <SpacesListStickyGroupRow
            key={virtualRow.key}
            node={node}
            level={level}
            columns={columns}
            isFirstColumnPinned={effectiveIsFirstColumnPinned}
            cellPerColumn={cellPerColumn}
            keyboardProps={groupKeyboardProps}
            measureRef={virtualizer.measureElement}
            dataIndex={virtualRow.index}
            isActiveSticky={isActiveSticky}
            evictionAnchor={evictionAnchor}
            tableRef={tableRef}
            className={needsTreeBorder ? styles.treeRow : undefined}
            style={guideStyle}
            ariaLevel={ariaLevel}
            ariaExpanded={ariaExpanded}
          />
        );
      }

      return (
        <TableSimpleRow
          key={virtualRow.key}
          itemId={node.id}
          item={node}
          columns={columns}
          isFirstColumnPinned={effectiveIsFirstColumnPinned}
          cellPerColumn={cellPerColumn}
          keyboardProps={rowKeyboardProps}
          measureRef={virtualizer.measureElement}
          dataIndex={virtualRow.index}
          className={cx({ [styles.treeRow]: needsTreeBorder })}
          style={guideStyle}
          ariaLevel={ariaLevel}
          ariaExpanded={ariaExpanded}
        />
      );
    },
    [
      nodes,
      expandedKeys,
      openGroupStarts,
      columns,
      effectiveIsFirstColumnPinned,
      cellPerColumn,
      groupKeyboardProps,
      rowKeyboardProps,
      tableRef,
      virtualizer.measureElement,
      virtualizer.measurementsCache,
      stickyGroupsVisibleOnTableRef,
    ]
  );

  const virtualizedItems = virtualizer.getVirtualItems();
  const [topGap, bottomGap] = calculateTreeVirtualPadding(
    virtualizedItems,
    virtualizer.getTotalSize(),
    virtualizer.options.scrollMargin,
    forcedStickyGroupsRef.current.size
  );

  return (
    <>
      {topGap > 0 && <SpacesListRowsSpacer height={topGap} />}
      {virtualizedItems.map(renderVirtualRow)}
      {bottomGap > 0 && <SpacesListRowsSpacer height={bottomGap} />}
    </>
  );
};

SpacesListRows.displayName = "SpacesListRows";

export default memo(SpacesListRows);
